import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRestaurant, useAccess } from '@/lib/session';
import { hasFeature, PLAN_LABEL, requiredTierFor } from '@/lib/access';
import { fetchOrders, fetchOrderItemsBetween, type Order } from '@/lib/data';

type Dimension = 'date' | 'dow' | 'hour' | 'paymentMode' | 'type' | 'item';
type Metric = 'orders' | 'gross' | 'aov' | 'units';

const DIMENSIONS: { key: Dimension; label: string }[] = [
  { key: 'date', label: 'Date' },
  { key: 'dow', label: 'Day of week' },
  { key: 'hour', label: 'Hour of day' },
  { key: 'paymentMode', label: 'Payment mode' },
  { key: 'type', label: 'Order type' },
  { key: 'item', label: 'Menu item' },
];
const METRICS: { key: Metric; label: string }[] = [
  { key: 'gross', label: 'Gross sales (₹)' },
  { key: 'orders', label: 'Order count' },
  { key: 'aov', label: 'Avg order value (₹)' },
  { key: 'units', label: 'Units sold' },
];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type Row = { key: string; orders: number; gross: number; units: number };

export default function ReportBuilderPage() {
  const restaurant = useRestaurant();
  const access = useAccess();
  const navigate = useNavigate();
  // Tally / accounting export is a Pro-only feature, gated separately from the
  // Growth-tier report-builder page it lives on.
  const tallyUnlocked = hasFeature(access.effectiveTier, 'tally');
  const tallyTier = requiredTierFor('tally') ?? 'pro';
  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);
  const [range, setRange] = useState({ from: weekAgo, to: today });
  const [dimension, setDimension] = useState<Dimension>('date');
  const [metric, setMetric] = useState<Metric>('gross');
  const [rows, setRows] = useState<Row[]>([]);
  const [ran, setRan] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const fromISO = new Date(`${range.from}T00:00`).toISOString();
      const toISO = new Date(`${range.to}T23:59:59`).toISOString();
      const map = new Map<string, Row>();
      const bump = (key: string, o: Partial<Row>) => {
        const r = map.get(key) ?? { key, orders: 0, gross: 0, units: 0 };
        r.orders += o.orders ?? 0; r.gross += o.gross ?? 0; r.units += o.units ?? 0;
        map.set(key, r);
      };

      if (dimension === 'item') {
        const items = await fetchOrderItemsBetween(restaurant.id, fromISO, toISO);
        for (const it of items) bump(it.name, { gross: (Number(it.price) || 0) * (it.qty || 0), units: it.qty || 0, orders: 0 });
      } else {
        const orders = await fetchOrders(restaurant.id, { from: fromISO, to: toISO, limit: 5000 });
        for (const o of orders as Order[]) {
          const d = new Date(o.placedAt);
          const key =
            dimension === 'date' ? o.placedAt.slice(0, 10)
              : dimension === 'dow' ? DOW[d.getDay()]
                : dimension === 'hour' ? `${String(d.getHours()).padStart(2, '0')}:00`
                  : dimension === 'paymentMode' ? (o.paymentMode || '—')
                    : (o.type || '—');
          bump(key, { orders: 1, gross: Number(o.total) || 0, units: o.itemCount || 0 });
        }
      }

      // Local value fn so this callback depends only on `metric` (already a dep)
      // and not on the component-scope `valueOf` (fixes react-hooks/exhaustive-deps).
      const valOf = (r: Row): number =>
        metric === 'orders' ? r.orders
          : metric === 'gross' ? r.gross
            : metric === 'units' ? r.units
              : r.orders ? r.gross / r.orders : 0;
      const sorted = [...map.values()].sort((a, b) => {
        if (dimension === 'date' || dimension === 'hour') return a.key.localeCompare(b.key);
        if (dimension === 'dow') return DOW.indexOf(a.key) - DOW.indexOf(b.key);
        return valOf(b) - valOf(a);
      });
      setRows(sorted); setRan(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not build the report.');
    } finally { setLoading(false); }
  }, [restaurant.id, range, dimension, metric]);

  const valueOf = (r: Row): number =>
    metric === 'orders' ? r.orders
      : metric === 'gross' ? r.gross
        : metric === 'units' ? r.units
          : r.orders ? r.gross / r.orders : 0;

  const fmt = (r: Row) => (metric === 'gross' || metric === 'aov')
    ? `₹${Math.round(valueOf(r)).toLocaleString('en-IN')}`
    : Math.round(valueOf(r)).toLocaleString('en-IN');

  const max = Math.max(1, ...rows.map(valueOf));
  const dimLabel = DIMENSIONS.find((d) => d.key === dimension)!.label;
  const metLabel = METRICS.find((m) => m.key === metric)!.label;

  const exportCsv = () => {
    const head = [dimLabel, metLabel];
    const body = rows.map((r) => [r.key, String(Math.round(valueOf(r)))]);
    const csv = [head, ...body].map((row) => row.map((c) => `"${c.replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `report-${dimension}-${metric}-${range.from}_${range.to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const [tallyBusy, setTallyBusy] = useState(false);
  const exportTally = async () => {
    setTallyBusy(true); setError(null);
    try {
      const fromISO = new Date(`${range.from}T00:00`).toISOString();
      const toISO = new Date(`${range.to}T23:59:59`).toISOString();
      const orders = await fetchOrders(restaurant.id, { from: fromISO, to: toISO, limit: 5000 });
      const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const tdate = (iso: string) => iso.slice(0, 10).replace(/-/g, '');
      const company = esc(restaurant.name || 'EzyDine');
      const vouchers = (orders as Order[]).map((o) => {
        const taxable = Number(o.subtotal) || 0;
        const cgst = Number(o.cgst) || 0, sgst = Number(o.sgst) || 0;
        const total = Number(o.total) || 0;
        return `   <TALLYMESSAGE xmlns:UDF="TallyUDF">
    <VOUCHER VCHTYPE="Sales" ACTION="Create">
     <DATE>${tdate(o.placedAt)}</DATE>
     <VOUCHERTYPENAME>Sales</VOUCHERTYPENAME>
     <VOUCHERNUMBER>${esc(o.id.slice(0, 8))}</VOUCHERNUMBER>
     <PARTYLEDGERNAME>${esc(o.customer || 'Walk-in')}</PARTYLEDGERNAME>
     <ALLLEDGERENTRIES.LIST><LEDGERNAME>${esc(o.customer || 'Walk-in')}</LEDGERNAME><ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE><AMOUNT>-${total.toFixed(2)}</AMOUNT></ALLLEDGERENTRIES.LIST>
     <ALLLEDGERENTRIES.LIST><LEDGERNAME>Sales</LEDGERNAME><ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE><AMOUNT>${taxable.toFixed(2)}</AMOUNT></ALLLEDGERENTRIES.LIST>
     <ALLLEDGERENTRIES.LIST><LEDGERNAME>Output CGST</LEDGERNAME><ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE><AMOUNT>${cgst.toFixed(2)}</AMOUNT></ALLLEDGERENTRIES.LIST>
     <ALLLEDGERENTRIES.LIST><LEDGERNAME>Output SGST</LEDGERNAME><ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE><AMOUNT>${sgst.toFixed(2)}</AMOUNT></ALLLEDGERENTRIES.LIST>
    </VOUCHER>
   </TALLYMESSAGE>`;
      }).join('\n');
      const xml = `<ENVELOPE>
 <HEADER><TALLYREQUEST>Import Data</TALLYREQUEST></HEADER>
 <BODY><IMPORTDATA>
  <REQUESTDESC><REPORTNAME>Vouchers</REPORTNAME><STATICVARIABLES><SVCURRENTCOMPANY>${company}</SVCURRENTCOMPANY></STATICVARIABLES></REQUESTDESC>
  <REQUESTDATA>
${vouchers}
  </REQUESTDATA>
 </IMPORTDATA></BODY>
</ENVELOPE>`;
      const url = URL.createObjectURL(new Blob([xml], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url; a.download = `tally-sales-${range.from}_${range.to}.xml`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Tally export failed.');
    } finally { setTallyBusy(false); }
  };

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-xl font-bold text-foreground-900">Report Builder</h1>
        <p className="text-sm text-foreground-500">Build a custom report — pick a period, a dimension, and a metric.</p>
      </div>

      <div className="bg-background-50 border border-background-200 rounded-lg p-5 flex flex-wrap gap-4 items-end">
        <label className="block"><span className="text-xs font-medium text-foreground-600">From</span>
          <input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })}
            className="mt-1 px-3 py-2 text-sm border border-background-300 rounded-lg" /></label>
        <label className="block"><span className="text-xs font-medium text-foreground-600">To</span>
          <input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })}
            className="mt-1 px-3 py-2 text-sm border border-background-300 rounded-lg" /></label>
        <label className="block"><span className="text-xs font-medium text-foreground-600">Group by</span>
          <select value={dimension} onChange={(e) => setDimension(e.target.value as Dimension)}
            className="mt-1 px-3 py-2 text-sm border border-background-300 rounded-lg">
            {DIMENSIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select></label>
        <label className="block"><span className="text-xs font-medium text-foreground-600">Measure</span>
          <select value={metric} onChange={(e) => setMetric(e.target.value as Metric)}
            className="mt-1 px-3 py-2 text-sm border border-background-300 rounded-lg">
            {METRICS.filter((m) => !(dimension === 'item' && m.key === 'aov')).map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select></label>
        <button onClick={run} disabled={loading}
          className="px-5 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer disabled:opacity-50">
          {loading ? 'Building…' : 'Build Report'}
        </button>
        {ran && rows.length > 0 && (
          <button onClick={exportCsv} className="px-4 py-2 text-sm font-medium rounded-lg border border-background-300 text-foreground-700 hover:bg-background-100 cursor-pointer">
            <i className="ri-download-line mr-1.5"></i>CSV
          </button>
        )}
        {tallyUnlocked ? (
          <button onClick={exportTally} disabled={tallyBusy} className="px-4 py-2 text-sm font-medium rounded-lg border border-background-300 text-foreground-700 hover:bg-background-100 cursor-pointer disabled:opacity-50">
            <i className="ri-file-excel-2-line mr-1.5"></i>{tallyBusy ? 'Exporting…' : 'Export to Tally (XML)'}
          </button>
        ) : (
          <button
            onClick={() => navigate('/dashboard/billing')}
            title={`Tally / accounting export is part of the ${PLAN_LABEL[tallyTier]} plan`}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-dashed border-accent-300 text-accent-600 hover:bg-accent-50 cursor-pointer"
          >
            <i className="ri-lock-2-line mr-1.5"></i>Export to Tally — Upgrade to {PLAN_LABEL[tallyTier]}
          </button>
        )}
      </div>

      {error && <div className="px-4 py-2 text-sm rounded-lg bg-red-100 text-red-700">{error}</div>}

      {!ran ? (
        <div className="text-center py-16 text-foreground-400">
          <i className="ri-bar-chart-grouped-line text-4xl"></i>
          <p className="mt-2 text-sm">Choose your options and build a report.</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-foreground-400">
          <i className="ri-inbox-line text-4xl"></i>
          <p className="mt-2 text-sm">No data in this period.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-background-200 max-w-3xl">
          <table className="w-full text-sm">
            <thead><tr className="bg-background-100 text-left text-foreground-600">
              <th className="p-3 font-medium">{dimLabel}</th>
              <th className="p-3 font-medium w-1/2">{metLabel}</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-background-100">
                  <td className="p-3 text-foreground-800">{r.key}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-background-100 overflow-hidden">
                        <div className="h-full bg-primary-500" style={{ width: `${(valueOf(r) / max) * 100}%` }}></div>
                      </div>
                      <span className="text-foreground-700 w-24 text-right tabular-nums">{fmt(r)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
